import { X } from "lucide-react";
import { fmtTime } from "@/lib/filters";

export default function EffectControls({ effect, duration, onChange, onRemove }) {
    if (!effect || !effect.def) return null;
    const def = effect.def;
    const maxDur = duration || 10;

    return (
        <div className="rounded-2xl border border-amberx/40 bg-amberx/5 p-4">
            <div className="flex items-center justify-between">
                <p className="font-mono text-[10px] font-bold tracking-[0.2em] text-amberx">
                    EFFECT · {def.name.toUpperCase()}
                </p>
                <button
                    data-testid="effect-remove-button"
                    onClick={onRemove}
                    aria-label="Remove effect"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-colors hover:border-emberx hover:text-emberx"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>

            <div className="mt-3 space-y-1.5">
                <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-300">Effect starts</span>
                    <span className="font-mono text-[11px] text-slate-400">{fmtTime(effect.start)}</span>
                </div>
                <input
                    type="range"
                    className="slider"
                    data-testid="effect-start-slider"
                    min={0}
                    max={Math.max(0.1, maxDur - 0.2)}
                    step={0.1}
                    value={effect.start}
                    onChange={(e) => {
                        const v = Math.min(Number(e.target.value), effect.end - 0.2);
                        onChange({ start: Math.max(0, v) });
                    }}
                />
                <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-300">Effect ends</span>
                    <span className="font-mono text-[11px] text-slate-400">{fmtTime(effect.end)}</span>
                </div>
                <input
                    type="range"
                    className="slider"
                    data-testid="effect-end-slider"
                    min={0.2}
                    max={maxDur}
                    step={0.1}
                    value={effect.end}
                    onChange={(e) => {
                        const v = Math.max(Number(e.target.value), effect.start + 0.2);
                        onChange({ end: Math.min(maxDur, v) });
                    }}
                />
            </div>

            <div className="mt-3 space-y-1.5">
                <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-300">Intensity</span>
                    <span className="font-mono text-[11px] text-amberx">{Math.round(effect.intensity * 100)}%</span>
                </div>
                <input
                    type="range"
                    className="slider"
                    data-testid="effect-intensity-slider"
                    min={0}
                    max={100}
                    step={1}
                    value={Math.round(effect.intensity * 100)}
                    onChange={(e) => onChange({ intensity: Number(e.target.value) / 100 })}
                />

                {def.controls.includes("amount") && (
                    <>
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-300">{def.amountLabel || "Amount"}</span>
                            <span className="font-mono text-[11px] text-amberx">{Math.round(effect.amount * 100)}%</span>
                        </div>
                        <input
                            type="range"
                            className="slider"
                            data-testid="effect-amount-slider"
                            min={0}
                            max={100}
                            step={1}
                            value={Math.round(effect.amount * 100)}
                            onChange={(e) => onChange({ amount: Number(e.target.value) / 100 })}
                        />
                    </>
                )}

                {def.controls.includes("speed") && (
                    <>
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-300">Speed</span>
                            <span className="font-mono text-[11px] text-amberx">{effect.speed.toFixed(1)}x</span>
                        </div>
                        <input
                            type="range"
                            className="slider"
                            data-testid="effect-speed-slider"
                            min={20}
                            max={300}
                            step={5}
                            value={Math.round(effect.speed * 100)}
                            onChange={(e) => onChange({ speed: Number(e.target.value) / 100 })}
                        />
                    </>
                )}

                <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-300">Opacity</span>
                    <span className="font-mono text-[11px] text-amberx">{Math.round(effect.opacity * 100)}%</span>
                </div>
                <input
                    type="range"
                    className="slider"
                    data-testid="effect-opacity-slider"
                    min={0}
                    max={100}
                    step={1}
                    value={Math.round(effect.opacity * 100)}
                    onChange={(e) => onChange({ opacity: Number(e.target.value) / 100 })}
                />
            </div>
        </div>
    );
}
